import React, { useEffect, useMemo, useRef, useState } from "react";

type Vec2 = { x: number; y: number };

type Node = {
  x: number;
  y: number;
  vx: number;
  vy: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export type DataNetworkBackgroundProps = {
  /** Number of nodes rendered on the canvas. If omitted, it adapts to viewport size. */
  nodeCount?: number;
  /** Max distance (px) to draw a link between two nodes. */
  linkDistance?: number;
  /** Magnet radius (px) around the cursor. */
  magnetRadius?: number;
  /** Background fill color. */
  background?: string;
  /** Node + link color. */
  color?: string;
  /** Base opacity for nodes (0-1). */
  nodeOpacity?: number;
  /** Base opacity for links (0-1). */
  linkOpacity?: number;
  /**
   * Optional: only show the canvas while the given section is in view.
   * Useful when you want a fullscreen fixed canvas limited to the Hero.
   */
  activeSectionId?: string;
  /** Opacity used when the section is not active. */
  inactiveOpacity?: number;
  className?: string;
};

/**
 * Fullscreen canvas background:
 * - slow floating nodes
 * - links when close
 * - cursor magnet interaction
 */
export default function DataNetworkBackground({
  nodeCount,
  linkDistance,
  magnetRadius = 150,
  background = "#0B0F19",
  color = "#00F2FE",
  nodeOpacity = 0.16,
  linkOpacity = 0.12,
  activeSectionId,
  inactiveOpacity = 0,
  className,
}: DataNetworkBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const nodesRef = useRef<Node[]>([]);
  const mouseRef = useRef<{ pos: Vec2 | null }>({ pos: null });
  const rafRef = useRef<number | null>(null);
  const [isActive, setIsActive] = useState(true);

  const getViewportTuning = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const area = w * h;

    const inferredNodeCount = clamp(Math.round(area / 28000), 18, 70);
    const inferredLinkDistance = w < 640 ? 90 : w < 1024 ? 115 : 140;
    const speedScale = w < 640 ? 0.75 : 1;

    return {
      nodeCount: nodeCount ?? inferredNodeCount,
      linkDistance: linkDistance ?? inferredLinkDistance,
      speedScale,
      w,
      h,
    };
  };

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    if (!activeSectionId) {
      setIsActive(true);
      return;
    }

    const target = document.getElementById(activeSectionId);
    if (!target) {
      setIsActive(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsActive(Boolean(entry?.isIntersecting));
      },
      { threshold: 0.15 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [activeSectionId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const setCanvasSize = () => {
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const { innerWidth, innerHeight } = window;

      canvas.width = Math.floor(innerWidth * dpr);
      canvas.height = Math.floor(innerHeight * dpr);
      canvas.style.width = `${innerWidth}px`;
      canvas.style.height = `${innerHeight}px`;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const resetNodes = () => {
      const tuning = getViewportTuning();
      const nodes: Node[] = [];

      for (let i = 0; i < tuning.nodeCount; i += 1) {
        nodes.push({
          x: Math.random() * tuning.w,
          y: Math.random() * tuning.h,
          vx: (Math.random() - 0.5) * 0.18 * tuning.speedScale,
          vy: (Math.random() - 0.5) * 0.18 * tuning.speedScale,
        });
      }

      nodesRef.current = nodes;
    };

    const handleResize = () => {
      setCanvasSize();
      resetNodes();
      drawFrame();
    };

    const handleMouseMove = (event: MouseEvent) => {
      mouseRef.current.pos = { x: event.clientX, y: event.clientY };
    };

    const handleMouseLeave = () => {
      mouseRef.current.pos = null;
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("mouseout", handleMouseLeave);

    setCanvasSize();
    resetNodes();

    const drawFrame = () => {
      const tuning = getViewportTuning();
      const { w: innerWidth, h: innerHeight } = tuning;
      const nodes = nodesRef.current;
      const mousePos = mouseRef.current.pos;

      if (background === "transparent" || background === "none") {
        ctx.clearRect(0, 0, innerWidth, innerHeight);
      } else {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, innerWidth, innerHeight);
      }

      // Update
      for (const node of nodes) {
        // Cursor magnet (small attraction)
        if (mousePos) {
          const dx = mousePos.x - node.x;
          const dy = mousePos.y - node.y;
          const distSq = dx * dx + dy * dy;
          const radiusSq = magnetRadius * magnetRadius;

          if (distSq < radiusSq) {
            const dist = Math.max(1, Math.sqrt(distSq));
            const strength = (1 - dist / magnetRadius) * 0.028 * tuning.speedScale;

            node.vx += (dx / dist) * strength;
            node.vy += (dy / dist) * strength;
          }
        }

        // Soft damping keeps it calm
        node.vx *= 0.985;
        node.vy *= 0.985;

        node.x += node.vx;
        node.y += node.vy;

        // Wrap around edges
        if (node.x < -20) node.x = innerWidth + 20;
        if (node.x > innerWidth + 20) node.x = -20;
        if (node.y < -20) node.y = innerHeight + 20;
        if (node.y > innerHeight + 20) node.y = -20;
      }

      // Links
      ctx.lineWidth = 1;
      ctx.strokeStyle = color;

      const currentLinkDistance = tuning.linkDistance;
      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);

          if (d > currentLinkDistance) continue;

          const proximity = 1 - d / currentLinkDistance;
          let alpha = linkOpacity * proximity;

          // Boost alpha near mouse
          if (mousePos) {
            const dax = a.x - mousePos.x;
            const day = a.y - mousePos.y;
            const dbx = b.x - mousePos.x;
            const dby = b.y - mousePos.y;
            const da = Math.sqrt(dax * dax + day * day);
            const db = Math.sqrt(dbx * dbx + dby * dby);
            const near = Math.max(0, 1 - Math.min(da, db) / magnetRadius);
            alpha += near * 0.12;
          }

          ctx.globalAlpha = Math.min(0.25, Math.max(0, alpha));
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // Nodes
      ctx.fillStyle = color;
      for (const node of nodes) {
        let alpha = nodeOpacity;

        if (mousePos) {
          const dx = node.x - mousePos.x;
          const dy = node.y - mousePos.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          const near = Math.max(0, 1 - d / magnetRadius);
          alpha += near * 0.12;
        }

        ctx.globalAlpha = Math.min(0.28, Math.max(0, alpha));
        ctx.beginPath();
        ctx.arc(node.x, node.y, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
    };

    const animate = () => {
      drawFrame();
      rafRef.current = window.requestAnimationFrame(animate);
    };

    if (prefersReducedMotion) {
      drawFrame();
    } else {
      animate();
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handleMouseLeave);

      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [background, color, linkDistance, linkOpacity, magnetRadius, nodeCount, nodeOpacity, prefersReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      className={
        className ??
        "fixed top-0 left-0 w-full h-full z-0 pointer-events-none transition-opacity duration-300"
      }
      style={{ opacity: isActive ? 1 : inactiveOpacity }}
      aria-hidden="true"
    />
  );
}
